'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type {
  OfficeWithPartialCity,
  SettlementWithClaimSectionAndOffice,
} from '@jlg-website/database/types';
import type { AttorneyInput } from '@jlg-website/shared/schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil } from 'lucide-react';

export const loading = () => <>Loading...</>;

const AttorneyForm = dynamic(
  () => import('@/components/admin/attorneys/attorney-form').then((mod) => mod.AttorneyForm),
  { loading }
);

type AttorneyFormData = {
  offices: OfficeWithPartialCity[];
  settlements: SettlementWithClaimSectionAndOffice[];
};

export function AttorneyEditDialogForm({ value, onEdit }: { value: string; onEdit: () => void }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<AttorneyFormData | null>(null);
  const [defaultValues, setDefaultValues] = useState<AttorneyInput | null>(null);

  const handleSave = () => {
    setOpen(false);
    onEdit();
  };

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      const [formDataRes, attorney] = await Promise.all([
        fetch('/admin/api/attorneys/form-data').then((r) => r.json()),
        fetch(`/admin/api/attorneys/${value}`).then((r) => r.json()),
      ]);
      setFormData(formDataRes);
      setDefaultValues(attorney);
    };

    fetchData();
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setFormData(null);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Attorney</DialogTitle>
          {formData && defaultValues && (
            <AttorneyForm
              offices={formData.offices}
              settlements={formData.settlements}
              defaultValues={defaultValues}
              onSave={handleSave}
              customCancelButton={
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              }
            />
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default AttorneyEditDialogForm;
